// De aantallen van één materiaalregel (AdressMaterials): wat er mee moet,
// wat er gebruikt is, wat er als impact telt en wat er van de voorraad af is.
//
// Nodig omdat een regel sinds 2026-10 twee aantallen kent ('Mee te nemen' en
// 'Gebruikt', per afspraak) naast het oude ene `Quantity`. Elke lezer van een
// aantal gaat hierdoorheen, zodat impact, besparing, export en voorraad
// dezelfde regel volgen. Spiegel in functions/materialsHelpers/materialQuantities.js,
// bewaakt door functions/materialsHelpers/materialQuantities.test.js.
//
// Besluiten Gijs:
// - Een gekoppeld materiaal telt meteen mee (2026-10-13): niet elke organisatie
//   vult 'gebruikt' in. Is 'gebruikt' ingevuld, dan telt dat preciezere aantal.
//   Een OUDE regel (zonder de nieuwe velden) telt zijn hele `Quantity`, welke
//   status ook: historische cijfers verschuiven niet.
// - Voorraad: eerst wat je meeneemt, daarna wat je gebruikt. Het effect van een
//   nieuwe regel op de voorraad is 'gebruikt' als dat is ingevuld, anders
//   'mee te nemen'. Een oude regel heeft het effect dat `Deducted` zegt: alleen
//   als zijn aantal echt van de voorraad af ging (14 levende regels uit de
//   oude subtaak-migratie staan op false). Zo'n regel blijft ook na omzetten
//   (Gebruikt invullen) buiten de voorraad: `Deducted: false` gaat mee.
//
// AI-CONTEXT: See docs/features/household-materials.md (Uitbreiding 2026-10)

/**
 * @typedef {object} MaterialQuantities
 * @property {boolean} legacy - Oude regel: alleen `Quantity`, geen QuantityPlanned/QuantityUsed
 * @property {number} planned - Mee te nemen (oude regel: `Quantity`)
 * @property {number | null} used - Gebruikt; null = nog niet ingevuld
 * @property {number} impact - Wat meetelt voor CO2, besparing en output
 * @property {number} stockEffect - Wat deze regel nu van de voorraad af heeft
 * @property {'toDo' | 'done'} status - Afgeleid voor nieuwe regels; oude regels houden hun eigen status
 */

/**
 * @param {*} waarde
 * @returns {number}
 */
const aantal = (waarde) => Number(waarde) || 0

/**
 * @param {*} waarde
 * @returns {boolean} Of er een aantal is ingevuld; 0 telt als ingevuld
 */
const ingevuld = (waarde) => waarde !== null && waarde !== undefined && waarde !== ''

/**
 * @param {object | null | undefined} regel - Een AdressMaterials-document
 * @returns {MaterialQuantities}
 */
const materialQuantities = (regel) => {
    const r = regel || {}
    const legacy = r.QuantityPlanned === undefined && r.QuantityUsed === undefined

    if (legacy) {
        const totaal = aantal(r.Quantity)
        const klaar = r.Status === 'done'
        return {
            legacy: true,
            planned: totaal,
            used: klaar ? totaal : null,
            impact: totaal,
            stockEffect: r.Deducted === true ? totaal : 0,
            status: klaar ? 'done' : 'toDo',
        }
    }

    const planned = aantal(r.QuantityPlanned)
    const used = ingevuld(r.QuantityUsed) ? aantal(r.QuantityUsed) : null
    return {
        legacy: false,
        planned,
        used,
        impact: used === null ? planned : used,
        // Een regel die nooit van de voorraad af ging (oude regel met Deducted:
        // false, daarna omgezet door 'Gebruikt' in te vullen) blijft erbuiten.
        stockEffect: r.Deducted === false ? 0 : (used === null ? planned : used),
        status: used === null ? 'toDo' : 'done',
    }
}

export default materialQuantities
